import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryMovies, categoryInfo } from '@/mocks/categoryData';
import { categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState('hot');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const info = slug ? categoryInfo[slug] : null;
  const movies = slug ? categoryMovies[slug] || [] : [];

  if (!info || movies.length === 0) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-lg">该分类暂无内容</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNameMap: Record<string, string> = {};
  categories.forEach((c) => { categoryNameMap[c.slug] = c.name; });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.beiwomovie.com/' },
      { '@type': 'ListItem', position: 2, name: info.name, item: `https://www.beiwomovie.com/category/${slug}` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="min-h-screen pt-16">
        {/* Category Header */}
        <section className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: info.name },
            ]} />

            <div className="flex items-center gap-4 mt-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className={`${info.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  被窝网{info.name}
                </h1>
                <p className="text-foreground-500 text-sm mt-1 max-w-2xl">
                  {info.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sub-category Tabs */}
        <section className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort Controls */}
        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-900">{movies.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1">
              {[
                { key: 'hot', label: '最热' },
                { key: 'new', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((sort) => (
                <button
                  key={sort.key}
                  onClick={() => setActiveSort(sort.key)}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    activeSort === sort.key
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {sort.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="w-full bg-background-50 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {movies.map((movie) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                    <img
                      src={movie.imageUrl}
                      alt={movie.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-0.5 px-2 py-0.5 bg-black/60 backdrop-blur-sm rounded-md text-yellow-400 text-xs font-medium">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-[10px]"></i>
                      </div>
                      {movie.rating}
                    </div>
                    {movie.tags && movie.tags.length > 0 && (
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {movie.tags.map((tag) => (
                          <span
                            key={tag}
                            className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                              tag === '热门'
                                ? 'bg-accent-500 text-white'
                                : tag === '新片'
                                  ? 'bg-primary-500 text-white'
                                  : tag === '高分'
                                    ? 'bg-secondary-500 text-white'
                                    : 'bg-foreground-800 text-white'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1 mb-1">
                    {movie.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                    <span>{movie.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span>{movie.region}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-foreground-500">{movie.category}</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More */}
            <div className="flex justify-center mt-10">
              <button className="px-8 py-3 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                加载更多
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}